import { defineStore } from 'pinia'
import { ref } from 'vue'
import { searchTasks } from '@/composables/services/useTasksApi'
import type { TaskSearchResponse } from '@/composables/services/useTasksApi'
import { buildTaskViewRequest } from '@/modules/inbox/utils/taskSearchRequests'
import { useUserStore } from '@/stores/user'
import type { Task } from '@/types/task'

const PAGE_SIZE = 25

export type TaskViewId = 'my-tasks' | 'my-tasks-due-today' | 'team-tasks' | 'all-tasks'

function getCtx() {
  const userStore = useUserStore()
  return {
    userId: userStore.userData?.id || '',
    orgId: userStore.userData?.object_id || '',
    teamIds: (userStore.userTeams || [])
      .map((team: any) => team?.team_id || team)
      .filter(Boolean) as string[],
  }
}

/** Prefer filtered count for paginator totalRecords; fall back when API omits it. */
function resolveTotal(res: TaskSearchResponse, start: number): number {
  const dataLength = res.data?.length ?? 0
  const fromMeta = res.meta.filtered || res.meta.total
  if (fromMeta > 0) return fromMeta
  if (res.meta.hasMore) return start + dataLength + 1
  return start + dataLength
}

export const useTasksStore = defineStore('tasks', () => {
  // Task lists
  const userTasks = ref<Task[]>([])
  const userTasksDueToday = ref<Task[]>([])
  const teamTasks = ref<Task[]>([])
  const allTasks = ref<Task[]>([])

  // Initial load state
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Per-view: hasMore / loadingMore for infinite scroll
  const hasMoreUserTasks = ref(false)
  const hasMoreUserTasksDueToday = ref(false)
  const hasMoreTeamTasks = ref(false)
  const hasMoreAllTasks = ref(false)

  // Per-view totals for table paginator (lazy / server-side)
  const totalUserTasks = ref(0)
  const totalUserTasksDueToday = ref(0)
  const totalTeamTasks = ref(0)
  const totalAllTasks = ref(0)

  const loadingMoreUserTasks = ref(false)
  const loadingMoreUserTasksDueToday = ref(false)
  const loadingMoreTeamTasks = ref(false)
  const loadingMoreAllTasks = ref(false)

  // Inflight deduplication — one request per view at a time
  let _userTasksInflight: Promise<void> | null = null
  let _userTasksDueTodayInflight: Promise<void> | null = null
  let _teamTasksInflight: Promise<void> | null = null
  let _allTasksInflight: Promise<void> | null = null

  function applyViewResult(viewId: TaskViewId, res: TaskSearchResponse, start: number, mode: 'replace' | 'append') {
    const data = res.data ?? []
    const total = resolveTotal(res, start)

    if (viewId === 'my-tasks') {
      if (mode === 'replace') {
        userTasks.value = data
      } else {
        const existingIds = new Set(userTasks.value.map(t => t.id))
        userTasks.value = [...userTasks.value, ...data.filter(t => !existingIds.has(t.id))]
      }
      hasMoreUserTasks.value = res.meta.hasMore
      totalUserTasks.value = total
      return
    }

    if (viewId === 'my-tasks-due-today') {
      if (mode === 'replace') {
        userTasksDueToday.value = data
      } else {
        const existingIds = new Set(userTasksDueToday.value.map(t => t.id))
        userTasksDueToday.value = [...userTasksDueToday.value, ...data.filter(t => !existingIds.has(t.id))]
      }
      hasMoreUserTasksDueToday.value = res.meta.hasMore
      totalUserTasksDueToday.value = total
      return
    }

    if (viewId === 'team-tasks') {
      if (mode === 'replace') {
        teamTasks.value = data
      } else {
        const existingIds = new Set(teamTasks.value.map(t => t.id))
        teamTasks.value = [...teamTasks.value, ...data.filter(t => !existingIds.has(t.id))]
      }
      hasMoreTeamTasks.value = res.meta.hasMore
      totalTeamTasks.value = total
      return
    }

    if (mode === 'replace') {
      allTasks.value = data
    } else {
      const existingIds = new Set(allTasks.value.map(t => t.id))
      allTasks.value = [...allTasks.value, ...data.filter(t => !existingIds.has(t.id))]
    }
    hasMoreAllTasks.value = res.meta.hasMore
    totalAllTasks.value = total
  }

  function clearView(viewId: TaskViewId) {
    if (viewId === 'my-tasks') {
      userTasks.value = []
      hasMoreUserTasks.value = false
      totalUserTasks.value = 0
    } else if (viewId === 'my-tasks-due-today') {
      userTasksDueToday.value = []
      hasMoreUserTasksDueToday.value = false
      totalUserTasksDueToday.value = 0
    } else if (viewId === 'team-tasks') {
      teamTasks.value = []
      hasMoreTeamTasks.value = false
      totalTeamTasks.value = 0
    } else {
      allTasks.value = []
      hasMoreAllTasks.value = false
      totalAllTasks.value = 0
    }
  }

  // ─── Initial load functions ────────────────────────────────────────────────

  async function loadUserTasks() {
    if (_userTasksInflight) return _userTasksInflight

    _userTasksInflight = (async () => {
      loading.value = true
      error.value = null
      try {
        const ctx = getCtx()
        const res = await searchTasks(buildTaskViewRequest('my-tasks', ctx, { limit: PAGE_SIZE, start: 0 }))
        applyViewResult('my-tasks', res, 0, 'replace')
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load user tasks'
      } finally {
        loading.value = false
        _userTasksInflight = null
      }
    })()

    return _userTasksInflight
  }

  async function loadUserTasksDueToday() {
    if (_userTasksDueTodayInflight) return _userTasksDueTodayInflight

    _userTasksDueTodayInflight = (async () => {
      loading.value = true
      error.value = null
      try {
        const ctx = getCtx()
        const res = await searchTasks(buildTaskViewRequest('my-tasks-due-today', ctx, { limit: PAGE_SIZE, start: 0 }))
        applyViewResult('my-tasks-due-today', res, 0, 'replace')
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load tasks due today'
      } finally {
        loading.value = false
        _userTasksDueTodayInflight = null
      }
    })()

    return _userTasksDueTodayInflight
  }

  async function loadTeamTasks() {
    if (_teamTasksInflight) return _teamTasksInflight

    _teamTasksInflight = (async () => {
      loading.value = true
      error.value = null
      try {
        const ctx = getCtx()
        if (!ctx.teamIds.length) {
          clearView('team-tasks')
          return
        }
        const res = await searchTasks(buildTaskViewRequest('team-tasks', ctx, { limit: PAGE_SIZE, start: 0 }))
        applyViewResult('team-tasks', res, 0, 'replace')
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load team tasks'
        console.error('TasksStore: Error in loadTeamTasks:', err)
      } finally {
        loading.value = false
        _teamTasksInflight = null
      }
    })()

    return _teamTasksInflight
  }

  async function loadAllTasks() {
    if (_allTasksInflight) return _allTasksInflight

    _allTasksInflight = (async () => {
      loading.value = true
      error.value = null
      try {
        const ctx = getCtx()
        const res = await searchTasks(buildTaskViewRequest('all-tasks', ctx, { limit: PAGE_SIZE, start: 0 }))
        applyViewResult('all-tasks', res, 0, 'replace')
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load all tasks'
        console.error('TasksStore: Error in loadAllTasks:', err)
      } finally {
        loading.value = false
        _allTasksInflight = null
      }
    })()

    return _allTasksInflight
  }

  // ─── Load more (infinite scroll append) ────────────────────────────────────

  async function loadMoreUserTasks() {
    if (loadingMoreUserTasks.value || !hasMoreUserTasks.value) return
    loadingMoreUserTasks.value = true
    try {
      const ctx = getCtx()
      const start = userTasks.value.length
      const res = await searchTasks(
        buildTaskViewRequest('my-tasks', ctx, { limit: PAGE_SIZE, start }),
      )
      applyViewResult('my-tasks', res, start, 'append')
    } catch (err) {
      console.error('TasksStore: Error in loadMoreUserTasks:', err)
    } finally {
      loadingMoreUserTasks.value = false
    }
  }

  async function loadMoreUserTasksDueToday() {
    if (loadingMoreUserTasksDueToday.value || !hasMoreUserTasksDueToday.value) return
    loadingMoreUserTasksDueToday.value = true
    try {
      const ctx = getCtx()
      const start = userTasksDueToday.value.length
      const res = await searchTasks(
        buildTaskViewRequest('my-tasks-due-today', ctx, { limit: PAGE_SIZE, start }),
      )
      applyViewResult('my-tasks-due-today', res, start, 'append')
    } catch (err) {
      console.error('TasksStore: Error in loadMoreUserTasksDueToday:', err)
    } finally {
      loadingMoreUserTasksDueToday.value = false
    }
  }

  async function loadMoreTeamTasks() {
    if (loadingMoreTeamTasks.value || !hasMoreTeamTasks.value) return
    loadingMoreTeamTasks.value = true
    try {
      const ctx = getCtx()
      const start = teamTasks.value.length
      const res = await searchTasks(
        buildTaskViewRequest('team-tasks', ctx, { limit: PAGE_SIZE, start }),
      )
      applyViewResult('team-tasks', res, start, 'append')
    } catch (err) {
      console.error('TasksStore: Error in loadMoreTeamTasks:', err)
    } finally {
      loadingMoreTeamTasks.value = false
    }
  }

  async function loadMoreAllTasks() {
    if (loadingMoreAllTasks.value || !hasMoreAllTasks.value) return
    loadingMoreAllTasks.value = true
    try {
      const ctx = getCtx()
      const start = allTasks.value.length
      const res = await searchTasks(
        buildTaskViewRequest('all-tasks', ctx, { limit: PAGE_SIZE, start }),
      )
      applyViewResult('all-tasks', res, start, 'append')
    } catch (err) {
      console.error('TasksStore: Error in loadMoreAllTasks:', err)
    } finally {
      loadingMoreAllTasks.value = false
    }
  }

  /**
   * Table pagination: replace the active view's list with a specific page.
   * Does not toggle the global loading spinner (caller can manage UI state).
   */
  async function loadTasksPage(
    viewId: TaskViewId,
    pagination: { limit: number; start: number },
  ) {
    const ctx = getCtx()
    if (viewId === 'team-tasks' && !ctx.teamIds.length) {
      clearView('team-tasks')
      return
    }

    try {
      const res = await searchTasks(buildTaskViewRequest(viewId, ctx, pagination))
      applyViewResult(viewId, res, pagination.start, 'replace')
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load tasks page'
      console.error('TasksStore: Error in loadTasksPage:', err)
      throw err
    }
  }

  // ─── Silent refresh (reset to page 1, no loading spinner) ─────────────────

  let _userTasksSilentInflight: Promise<void> | null = null
  let _userTasksDueTodaySilentInflight: Promise<void> | null = null
  let _teamTasksSilentInflight: Promise<void> | null = null
  let _allTasksSilentInflight: Promise<void> | null = null

  async function loadUserTasksSilently() {
    if (_userTasksSilentInflight) return _userTasksSilentInflight

    _userTasksSilentInflight = (async () => {
      try {
        const ctx = getCtx()
        const res = await searchTasks(buildTaskViewRequest('my-tasks', ctx, { limit: PAGE_SIZE, start: 0 }))
        applyViewResult('my-tasks', res, 0, 'replace')
      } catch (err) {
        console.error('TasksStore: Error in loadUserTasksSilently:', err)
      } finally {
        _userTasksSilentInflight = null
      }
    })()

    return _userTasksSilentInflight
  }

  async function loadUserTasksDueTodaySilently() {
    if (_userTasksDueTodaySilentInflight) return _userTasksDueTodaySilentInflight

    _userTasksDueTodaySilentInflight = (async () => {
      try {
        const ctx = getCtx()
        const res = await searchTasks(buildTaskViewRequest('my-tasks-due-today', ctx, { limit: PAGE_SIZE, start: 0 }))
        applyViewResult('my-tasks-due-today', res, 0, 'replace')
      } catch (err) {
        console.error('TasksStore: Error in loadUserTasksDueTodaySilently:', err)
      } finally {
        _userTasksDueTodaySilentInflight = null
      }
    })()

    return _userTasksDueTodaySilentInflight
  }

  async function loadTeamTasksSilently() {
    if (_teamTasksSilentInflight) return _teamTasksSilentInflight

    _teamTasksSilentInflight = (async () => {
      try {
        const ctx = getCtx()
        if (!ctx.teamIds.length) {
          clearView('team-tasks')
          return
        }
        const res = await searchTasks(buildTaskViewRequest('team-tasks', ctx, { limit: PAGE_SIZE, start: 0 }))
        applyViewResult('team-tasks', res, 0, 'replace')
      } catch (err) {
        console.error('TasksStore: Error in loadTeamTasksSilently:', err)
      } finally {
        _teamTasksSilentInflight = null
      }
    })()

    return _teamTasksSilentInflight
  }

  async function loadAllTasksSilently() {
    if (_allTasksSilentInflight) return _allTasksSilentInflight

    _allTasksSilentInflight = (async () => {
      try {
        const ctx = getCtx()
        const res = await searchTasks(buildTaskViewRequest('all-tasks', ctx, { limit: PAGE_SIZE, start: 0 }))
        applyViewResult('all-tasks', res, 0, 'replace')
      } catch (err) {
        console.error('TasksStore: Error in loadAllTasksSilently:', err)
      } finally {
        _allTasksSilentInflight = null
      }
    })()

    return _allTasksSilentInflight
  }

  function resetSessionState() {
    userTasks.value = []
    userTasksDueToday.value = []
    teamTasks.value = []
    allTasks.value = []
    hasMoreUserTasks.value = false
    hasMoreUserTasksDueToday.value = false
    hasMoreTeamTasks.value = false
    hasMoreAllTasks.value = false
    totalUserTasks.value = 0
    totalUserTasksDueToday.value = 0
    totalTeamTasks.value = 0
    totalAllTasks.value = 0
    _userTasksInflight = null
    _userTasksDueTodayInflight = null
    _teamTasksInflight = null
    _allTasksInflight = null
    _userTasksSilentInflight = null
    _userTasksDueTodaySilentInflight = null
    _teamTasksSilentInflight = null
    _allTasksSilentInflight = null
  }

  return {
    userTasks,
    userTasksDueToday,
    teamTasks,
    allTasks,
    loading,
    error,
    // hasMore
    hasMoreUserTasks,
    hasMoreUserTasksDueToday,
    hasMoreTeamTasks,
    hasMoreAllTasks,
    // totals (table paginator)
    totalUserTasks,
    totalUserTasksDueToday,
    totalTeamTasks,
    totalAllTasks,
    // loadingMore
    loadingMoreUserTasks,
    loadingMoreUserTasksDueToday,
    loadingMoreTeamTasks,
    loadingMoreAllTasks,
    // initial load
    loadUserTasks,
    loadUserTasksDueToday,
    loadTeamTasks,
    loadAllTasks,
    // load more
    loadMoreUserTasks,
    loadMoreUserTasksDueToday,
    loadMoreTeamTasks,
    loadMoreAllTasks,
    // table page replace
    loadTasksPage,
    // silent refresh
    loadUserTasksSilently,
    loadUserTasksDueTodaySilently,
    loadTeamTasksSilently,
    loadAllTasksSilently,
    resetSessionState,
  }
})
